import { isRainNight, cardIdOf } from './config.js';

export const AUDIO_PREFS_KEY = 'ninetynine.audio.v1';
export const AUDIO_PREFS_DEFAULT = Object.freeze({
  muted: false,
  master: 0.82,
  sfx: 0.86,
  ambience: 0.72,
  ui: 0.80,
});

const clamp01 = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : fallback;
};

export function normalizeAudioPrefs(value = {}) {
  const v = value && typeof value === 'object' ? value : {};
  return {
    muted: typeof v.muted === 'boolean' ? v.muted : AUDIO_PREFS_DEFAULT.muted,
    master: clamp01(v.master, AUDIO_PREFS_DEFAULT.master),
    sfx: clamp01(v.sfx, AUDIO_PREFS_DEFAULT.sfx),
    ambience: clamp01(v.ambience, AUDIO_PREFS_DEFAULT.ambience),
    ui: clamp01(v.ui, AUDIO_PREFS_DEFAULT.ui),
  };
}

export function loadAudioPrefs(storage = globalThis.localStorage) {
  try {
    return normalizeAudioPrefs(JSON.parse(storage?.getItem(AUDIO_PREFS_KEY) ?? '{}'));
  } catch {
    return normalizeAudioPrefs();
  }
}

export function saveAudioPrefs(value, storage = globalThis.localStorage) {
  const prefs = normalizeAudioPrefs(value);
  try { storage?.setItem(AUDIO_PREFS_KEY, JSON.stringify(prefs)); } catch { /* 저장 불가 환경 */ }
  return prefs;
}
// ══════════════════════════════════════════════════════════════════
//  사운드 큐 + 실시간 전투 사운드 스택
//
//  ■ 지금 이 저장소에 녹음 에셋은 0개다. 그래서 두 층으로 나눴다:
//      ① 큐 표(CUES)      — 번호·트리거·기능·우선순위·시각 이중화. **문서 그대로.**
//      ② 실시간 합성      — 무기 발사·접촉·처치·획득·피격·비/바람을 다층으로 만든다.
//    서사 큐의 단순 voice는 녹음 에셋이 들어오면 교체하지만, 전투 스택은 밀집도 집계,
//    스테레오 위치, 컴프레서와 함께 동작하는 현재의 사운드 디자인 구현이다.
//
//  ■ **큐 번호는 재번호하지 않는다**(디자인방향 399행). 삭제된 3·4·5·8·10·11·13은
//    비운 채로 둔다 — 30밤시트 카드들이 번호로 큐를 참조하고 있다.
//
//  ■ **셋은 연출이 아니라 판정이다** — 없으면 그 밤의 설계가 반쪽이 된다:
//      14/17 돌진 예고  · 9 새끼 울음(N22 방향 패널의 청각 이중화) · 1 하울링(본대 예고)
//
//  ■ 우선순위 = 동시 재생 시 **상위가 하위를 덕킹(−50%)**(디자인방향 359행).
//    숫자가 작을수록 상위다.
// ══════════════════════════════════════════════════════════════════

// ■ **아래 표에서 정본은 `n` · `ko` · `trigger` · `info` · `pri` · `dual` 여섯 칸뿐이다**
//   (디자인방향 10장 표를 그대로 옮긴 것). 나머지는 전부 **임의값 — 미검증**이다:
//     · `voice` 전량(f0/f1/dur/gain/vib) — 합성 자리표시자. 파일이 오면 통째로 죽는다
//     · `minGap` — 겹침을 버리는 최소 간격. 근거는 «같은 신호가 동시에 둘 이상 들려도
//       플레이어가 읽는 정보는 하나»뿐이고 **0.25/0.10 이라는 수 자체는 근거가 없다**
//     · `silence.pri: 0` — 디자인방향은 큐 16의 우선순위를 숫자가 아니라 *"(부분 덕킹)"*
//       으로 적는다. 숫자가 필요해서 0을 넣었을 뿐 문서가 준 값이 아니다
// kind: 'one' 단발 · 'loop' 지속(음량을 밖에서 계속 먹인다)
// dual: 옵트인 시각 대체. base:true 면 **접근성 모드와 무관하게 원래 있는 시각 정보**다.
export const CUES = {
  howl:    { n: 1,  ko: '하울링',      kind: 'one',  pri: 1, dual: '화면 가장자리 파동',
             trigger: '본대 도착 직전', info: '웨이브 예고',
             url: null, voice: { type: 'sine', f0: 220, f1: 330, dur: 1.6, gain: 0.35, vib: 5 } },
  bark:    { n: 2,  ko: '개 짖음',      kind: 'one',  pri: 4, dual: null, minGap: 0.25,
             trigger: '개의 교전', info: '동료 존재감과 전장 분위기',
             url: null, voice: { type: 'square', f0: 420, f1: 260, dur: 0.12, gain: 0.10 } },
  flock:   { n: 6,  ko: '무리 소리',    kind: 'loop', pri: 5, dual: 'HUD 안정성 바 진폭',
             trigger: '상시', info: '화면 밖 무리의 규모 — 위치는 전달하지 않는다',
             url: null, voice: { type: 'triangle', f0: 148, dur: 0, gain: 0.05, vib: 0.7 } },
  breath:  { n: 7,  ko: '숨소리',       kind: 'loop', pri: 2, dual: '화면 가장자리 어두워짐',
             dualBase: true, trigger: 'HP 저하(주 채널)', info: 'HP 상태',
             url: null, voice: { type: 'noise', f0: 520, dur: 0, gain: 0.16 } },
  lamb:    { n: 9,  ko: '새끼 울음',    kind: 'loop', pri: 1, dual: '방향 파문',
             trigger: '마지막 밤 — 방향 화살표 등장 이후',   // 구 N22 → 40일 이관(v3.1)
             info: '가까울수록 커진다 · **발견 직전 2초 그친다**',
             url: null, voice: { type: 'sawtooth', f0: 660, dur: 0, gain: 0.22, vib: 6 } },
  lyre:    { n: 12, ko: '수금',         kind: 'one',  pri: 5, dual: null,
             trigger: '평온(적 없음·안정성 높음)', info: '평온 확인',
             url: null, voice: { type: 'sine', f0: 587, f1: 880, dur: 0.9, gain: 0.10 } },
  bearDig: { n: 14, ko: '곰 땅 긁기',   kind: 'one',  pri: 1, dual: '곰 전방 지면 표식',
             trigger: '돌진 예고', info: '**회피 신호**',
             url: null, voice: { type: 'noise', f0: 180, dur: 0.5, gain: 0.34 } },
  dawn:    { n: 15, ko: '첫 새소리',    kind: 'one',  pri: 1, dual: '화면 밝기 전환',
             trigger: '밤 종료', info: '생존 확정',
             url: null, voice: { type: 'sine', f0: 1320, f1: 1760, dur: 0.5, gain: 0.20 } },
  silence: { n: 16, ko: '침묵',         kind: 'flag', pri: 0, dual: '화면 채도 미세 하강',
             trigger: '사자 활성', info: '**부재가 신호** — 1·12·2를 소거. 6은 끄지 않는다',
             url: null, voice: null },
};

const HUSHED_BY_SILENCE = ['howl', 'lyre', 'bark'];   // 큐 16 — **flock 은 뺀다**(자해 방지)

// 화면 전체 채널과 같은 여섯 버스. 소리를 없애지 않고 상위 사건이 울리는 짧은 동안만
// 하위 버스를 내린다. 위험·피격은 언제나 맨 앞이고 flock/환경음은 맨 뒤다.
export const AUDIO_LAYERS = Object.freeze(['danger', 'boss', 'reward', 'combat', 'utility', 'ambient']);
const AUDIO_LAYER_RANK = Object.freeze({ ambient: 1, utility: 2, combat: 3, reward: 4, boss: 4, danger: 5 });
export const AUDIO_DUCK_PROFILE = Object.freeze({
  danger: Object.freeze({ danger: 1, boss: 0.72, reward: 0.64, combat: 0.48, utility: 0.38, ambient: 0.26 }),
  boss:   Object.freeze({ danger: 1, boss: 1, reward: 0.82, combat: 0.58, utility: 0.42, ambient: 0.30 }),
  reward: Object.freeze({ danger: 1, boss: 1, reward: 1, combat: 0.64, utility: 0.46, ambient: 0.34 }),
  combat: Object.freeze({ danger: 1, boss: 1, reward: 1, combat: 1, utility: 0.72, ambient: 0.52 }),
  utility:Object.freeze({ danger: 1, boss: 1, reward: 1, combat: 1, utility: 1, ambient: 0.72 }),
  ambient:Object.freeze({ danger: 1, boss: 1, reward: 1, combat: 1, utility: 1, ambient: 1 }),
});

// 번호가 붙은 서사 큐와 별개의 전투 행동 문법. 한 행동군은 tell/commit/recover에서
// 같은 재질을 공유하되, 회피를 요구하는 tell(및 무예고 요격)만 danger 버스를 쓴다.
// gap은 개체별이 아니라 행동군 전체에 적용되어 수백 마리를 한 무리의 소리로 접는다.
const behaviorProfile = (stages) => Object.freeze(Object.fromEntries(
  Object.entries(stages).map(([stage, value]) => [stage, Object.freeze(value)]),
));
export const BEHAVIOR_AUDIO_PROFILES = Object.freeze({
  charge: behaviorProfile({
    tell:    { group: 'danger', gap: 0.55, focus: 0.30 },
    commit:  { group: 'combat', gap: 0.34, focus: 0.14 },
    recover: { group: 'combat', gap: 0.82, focus: 0.10 },
  }),
  intercept: behaviorProfile({
    // 자칼은 정지 tell이 없다. 잠긴 선을 닫기 시작하는 이 한 박자가 회피 신호다.
    commit:  { group: 'danger', gap: 0.42, focus: 0.18 },
    recover: { group: 'combat', gap: 0.86, focus: 0.09 },
  }),
  pack: behaviorProfile({
    tell:    { group: 'danger', gap: 0.90, focus: 0.32 },
    commit:  { group: 'combat', gap: 0.58, focus: 0.13 },
    recover: { group: 'combat', gap: 1.05, focus: 0.09 },
  }),
  wing: behaviorProfile({
    tell:    { group: 'danger', gap: 0.76, focus: 0.26 },
    commit:  { group: 'combat', gap: 0.56, focus: 0.12 },
    recover: { group: 'combat', gap: 0.92, focus: 0.08 },
  }),
  bite: behaviorProfile({
    commit:  { group: 'combat', gap: 0.34, focus: 0.11 },
    recover: { group: 'combat', gap: 0.82, focus: 0.07 },
  }),
  kick: behaviorProfile({
    commit:  { group: 'combat', gap: 0.42, focus: 0.13 },
    recover: { group: 'combat', gap: 0.86, focus: 0.07 },
  }),
  talon: behaviorProfile({
    tell:    { group: 'danger', gap: 0.76, focus: 0.26 },
    commit:  { group: 'combat', gap: 0.56, focus: 0.14 },
    recover: { group: 'combat', gap: 0.92, focus: 0.08 },
  }),
});
export const behaviorAudioProfile = (family, stage) =>
  BEHAVIOR_AUDIO_PROFILES[family]?.[stage] ?? null;
// Family gaps keep each material distinct; this second, stage-wide gate stops
// alternating species from bypassing every family timer in a dense frame run.
// Values are derived from the fastest authored profile in each scene bucket.
export const BEHAVIOR_AUDIO_LANE_GAP = Object.freeze(Object.fromEntries(
  ['tell', 'commit', 'recover'].map((stage) => [stage, Math.min(
    ...Object.values(BEHAVIOR_AUDIO_PROFILES)
      .map((profile) => profile[stage]?.gap)
      .filter(Number.isFinite),
  )]),
));
const CUE_AUDIO_LAYER = Object.freeze({
  howl: 'danger', breath: 'danger', lamb: 'danger', bearDig: 'danger',
  dawn: 'reward', bark: 'utility', flock: 'ambient', lyre: 'ambient', silence: 'danger',
});
export const cueAudioLayer = (id) => CUE_AUDIO_LAYER[id] ?? 'combat';

// HP feedback owns one stable 0..1 signal. Keeping this outside the scene's
// general cue order prevents VFX/audio-priority refactors from silently
// disconnecting the low-health breathing loop again.
export function lowHealthBreathMix(hp, maxHp) {
  const cap = Math.max(1, Number(maxHp) || 0);
  const ratio = Math.max(0, Math.min(1, (Number(hp) || 0) / cap));
  return ratio < 0.5 ? (0.5 - ratio) / 0.5 : 0;
}

// A restrained bed for every authored night. Combat transients remain in front;
// these layers give the field a place, temperature and pressure even before the
// first enemy reaches the player.
// ⚠⚠ **열쇠는 날짜가 아니라 「구 카드 번호」다** 【40일 개편 2026-08-03】.
//   이 표는 앵커 열 밤에 맞춰 만든 것이고, 그 열쇠(N1·N3·N8…)는 **그 카드**를 뜻한다.
//   40일로 옮기면서 같은 이름의 날짜가 딴 밤이 됐으므로, 찾을 때 `cardIdOf()` 로
//   그 밤이 **어느 카드에서 왔는지**를 먼저 되찾는다. 안 그러면 협곡의 밤에서
//   빗소리가 나고 비의 밤은 조용해진다.
//   ※ 구 N22(잃은 양) 줄은 카드가 삭제되어 같이 지웠다.
const AMBIENCE = {
  N1:  { master: 0.030, airType: 'bandpass', airFreq: 980, bodyFreq: 210, rate: 0.68, drone: 0 },
  N3:  { master: 0.036, airType: 'bandpass', airFreq: 620, bodyFreq: 170, rate: 0.57, drone: 0 },
  N8:  { master: 0.042, airType: 'lowpass',  airFreq: 310, bodyFreq: 115, rate: 0.43, drone: 52 },
  N10: { master: 0.048, airType: 'bandpass', airFreq: 430, bodyFreq: 145, rate: 0.78, drone: 0 },
  N12: { master: 0.040, airType: 'bandpass', airFreq: 760, bodyFreq: 125, rate: 0.52, drone: 61 },
  N15: { master: 0.110, airType: 'highpass', airFreq: 3100, bodyFreq: 430, rate: 0.73, drone: 0 },
  N20: { master: 0.045, airType: 'lowpass',  airFreq: 370, bodyFreq: 105, rate: 0.48, drone: 47 },
  N23: { master: 0.070, airType: 'highpass', airFreq: 1850, bodyFreq: 330, rate: 0.92, drone: 0 },
  N30: { master: 0.062, airType: 'lowpass',  airFreq: 260, bodyFreq: 82,  rate: 0.39, drone: 36 },
};

// ── AudioContext 는 **프로세스에 하나뿐이다** ──────────────────────
// 씬은 밤을 다시 시작할 때마다 새로 create() 되는데, 그때마다 컨텍스트를 새로 만들면
// **브라우저 상한(크롬 6개)에 부딪혀 7번째 밤부터 페이지가 죽는다** — 그것도 매 프레임
// 도는 ensure() 안에서 던지므로 게임이 통째로 멈춘다. 컨텍스트는 모듈이 들고 있고
// 인스턴스는 빌려 쓴다. (close() 로 닫았다 다시 여는 것도 답이 아니다 — 재생성은
//  수십 ms가 걸리고 밤 시작마다 그 비용을 낸다.)
let CTX = null, BUS = null, LIMITER = null;
function sharedCtx(master, externalContext = null, destination = null) {
  if (CTX) return CTX;
  if (externalContext) CTX = externalContext;
  else {
    const AC = globalThis.AudioContext || globalThis.webkitAudioContext;
    if (!AC) return null;
    CTX = new AC();
  }
  BUS = CTX.createGain();
  BUS.gain.value = master;
  // Dense combat can legitimately stack dozens of transients. A fast
  // compressor keeps that density physical without clipping the browser output.
  LIMITER = CTX.createDynamicsCompressor();
  LIMITER.threshold.value = -13;
  LIMITER.knee.value = 16;
  LIMITER.ratio.value = 8;
  LIMITER.attack.value = 0.003;
  LIMITER.release.value = 0.17;
  BUS.connect(LIMITER);
  LIMITER.connect(destination ?? CTX.destination);
  return CTX;
}

export class AudioCues {
  constructor(opts = {}) {
    this.on = opts.on ?? true;
    this.prefs = normalizeAudioPrefs(opts.prefs ?? loadAudioPrefs(opts.storage));
    if (opts.master != null) this.prefs.master = clamp01(opts.master, this.prefs.master);
    this.master = this.prefs.master;
    this.a11y = opts.a11y ?? false;      // 청각 이중화(옵트인). dualBase 는 이것과 무관
    this._context = opts.context ?? null;
    this._destination = opts.destination ?? null;
    this._storage = opts.storage ?? globalThis.localStorage;
    this.silence = false;                // 큐 16
    this.ctx = null;
    this.loops = {};                     // id → { osc, gain, want }
    this.until = {};                     // pri → 그 우선순위가 울리고 있는 시각(초)
    this.m = { played: {}, blocked: 0 };
    this._sfxLast = {};
    this.mix = null;
    this.trim = null;
    this.pauseGate = null;
    this.pauseMode = 'none';
    this._focusRank = 0;
    this._focusUntil = 0;
  }

  // 브라우저 자동재생 정책 — **첫 입력 뒤에만 소리가 난다.** 밤 시작이 곧 클릭이므로
  // 실사용에서는 걸리지 않지만, 걸렸을 때 조용히 실패하지 않게 센다.
  ensure() {
    if (!this.on || this.prefs.muted) return null;
    if (!this.ctx) {
      const c = sharedCtx(this.master, this._context, this._destination);
      if (!c) { this.on = false; return null; }
      this.ctx = c; this.bus = BUS;
      this.initMix();
      this.applyOutputGain(true);
    }
    // **resume 은 매 프레임 부르지 않는다.** 자동재생 정책에 막힌 동안 60번/초
    // 호출하면 대기 중인 Promise 만 쌓이고, m.blocked 가 「막힌 횟수」가 아니라
    // 「막힌 프레임 수」가 되어 지표로도 못 쓴다. 한 번 걸고 결과를 기다린다.
    if (this.ctx.state === 'suspended' && !this._resuming) {
      this._resuming = true; this.m.blocked++;
      this.ctx.resume().then(() => { this._resuming = false; },
                             () => { this._resuming = false; });
    }
    return this.ctx;
  }
  setOn(v) {
    this.on = !!v;
    this.applyOutputGain(true);
    if (!this.on) this.stopAll();
  }

  settings() { return { ...this.prefs, enabled: this.on, pauseMode: this.pauseMode }; }

  diagnostics() {
    return {
      ...this.settings(),
      contextState: this.ctx?.state ?? 'not-created',
      sampleRate: this.ctx?.sampleRate ?? null,
      baseLatency: this.ctx?.baseLatency ?? null,
      outputLatency: this.ctx?.outputLatency ?? null,
      outputChannels: this.ctx?.destination?.maxChannelCount ?? null,
      usesInjectedContext: !!this._context && this.ctx === this._context,
      layerCount: this.mix ? Object.keys(this.mix).length : 0,
      loops: Object.keys(this.loops).length,
      ambienceActive: !!this._ambience,
      blockedResumes: this.m.blocked,
      behaviorPlayed: { ...(this.m.behaviorPlayed ?? {}) },
      behaviorDropped: { ...(this.m.behaviorDropped ?? {}) },
      bossActions: { ...(this.m.bossActions ?? {}) },
    };
  }

  persistPrefs() {
    this.prefs = saveAudioPrefs(this.prefs, this._storage);
    this.master = this.prefs.master;
  }

  applyOutputGain(immediate = false) {
    if (!this.bus || !this.ctx) return;
    const value = this.on && !this.prefs.muted ? this.prefs.master : 0;
    const t = this.ctx.currentTime;
    const param = this.bus.gain;
    param.cancelScheduledValues(t);
    if (immediate) param.setValueAtTime(value, t);
    else param.setTargetAtTime(value, t, value > 0 ? 0.025 : 0.008);
  }

  applyTrim(immediate = false) {
    if (!this.trim || !this.ctx) return;
    const t = this.ctx.currentTime;
    for (const id of AUDIO_LAYERS) {
      const value = id === 'ambient' ? this.prefs.ambience
        : id === 'utility' ? this.prefs.ui : this.prefs.sfx;
      const param = this.trim[id].gain;
      param.cancelScheduledValues(t);
      if (immediate) param.setValueAtTime(value, t);
      else param.setTargetAtTime(value, t, 0.025);
    }
  }

  setMuted(value) {
    const muted = !!value;
    if (this.prefs.muted === muted) return muted;
    this.prefs.muted = muted;
    this.persistPrefs();
    this.applyOutputGain(true);
    if (muted) this.stopAll();
    else this.ensure();
    return muted;
  }

  toggleMuted() { return this.setMuted(!this.prefs.muted); }

  setVolume(channel, value) {
    if (!['master', 'sfx', 'ambience', 'ui'].includes(channel)) return false;
    this.prefs[channel] = clamp01(value, this.prefs[channel]);
    this.persistPrefs();
    if (channel === 'master') this.applyOutputGain();
    else this.applyTrim();
    return true;
  }

  setPaused(mode = 'pause') {
    const next = mode === false || mode === 'none' ? 'none'
      : mode === 'shop' ? 'shop'
        : mode === 'hidden' ? 'hidden' : 'pause';
    if (next === this.pauseMode) return;
    this.pauseMode = next;
    if (!this.pauseGate || !this.ctx) return;
    const t = this.ctx.currentTime;
    if (next === 'shop') {
      // 전투가 멎은 뒤에도 직전 하울링/천둥의 긴 덕킹 예약이 작업대 UI를
      // 누르지 않게, 상점 경계에서 전투 포커스를 닫는다.
      this._focusRank = 0;
      this._focusUntil = 0;
      for (const id of AUDIO_LAYERS) {
        const duck = this.mix[id].gain;
        duck.cancelScheduledValues(t);
        duck.setValueAtTime(1, t);
      }
    }
    for (const id of AUDIO_LAYERS) {
      // 작업대가 열린 동안에는 전장을 고요하게 하되 버튼의 재질음은 남긴다.
      const value = next === 'none' || (next === 'shop' && id === 'utility') ? 1 : 0;
      const param = this.pauseGate[id].gain;
      param.cancelScheduledValues(t);
      if (next === 'hidden') param.setValueAtTime(value, t);
      else param.setTargetAtTime(value, t, value > 0 ? 0.035 : 0.010);
    }
  }

  initMix() {
    if (this.mix || !this.ctx) return;
    this.mix = {};
    this.trim = {};
    this.pauseGate = {};
    for (const id of AUDIO_LAYERS) {
      const duck = this.ctx.createGain();
      const trim = this.ctx.createGain();
      const pause = this.ctx.createGain();
      duck.gain.value = 1;
      trim.gain.value = 1;
      pause.gain.value = 1;
      duck.connect(trim);
      trim.connect(pause);
      pause.connect(this.bus);
      this.mix[id] = duck;
      this.trim[id] = trim;
      this.pauseGate[id] = pause;
    }
    this.applyTrim(true);
    const paused = this.pauseMode;
    this.pauseMode = '__init__';
    this.setPaused(paused);
  }

  busFor(group = 'combat') { return this.mix?.[group] ?? this.bus; }

  focus(group, dur = 0.12) {
    const ctx = this.ctx;
    if (!ctx || !this.mix) return;
    const rank = AUDIO_LAYER_RANK[group] ?? AUDIO_LAYER_RANK.combat;
    const t = ctx.currentTime;
    if (this._focusUntil > t && rank < this._focusRank) return;
    const until = Math.max(t + Math.max(0.04, dur), rank === this._focusRank ? this._focusUntil : 0);
    this._focusRank = rank;
    this._focusUntil = until;
    const profile = AUDIO_DUCK_PROFILE[group] ?? AUDIO_DUCK_PROFILE.combat;
    for (const id of AUDIO_LAYERS) {
      const param = this.mix[id].gain;
      if (typeof param.cancelAndHoldAtTime === 'function') param.cancelAndHoldAtTime(t);
      else {
        const held = Math.max(0.0001, Number(param.value) || 1);
        param.cancelScheduledValues(t);
        param.setValueAtTime(held, t);
      }
      param.setTargetAtTime(profile[id] ?? 1, t, 0.012);
      param.setTargetAtTime(1, until, 0.065);
    }
  }

  setSilence(active) {
    const next = !!active;
    if (this.silence === next) return;
    this.silence = next;
    if (next && this.ctx) {
      for (const id of HUSHED_BY_SILENCE) {
        const L = this.loops[id];
        if (L) L.gain.gain.setTargetAtTime(0.0001, this.ctx.currentTime, 0.05);
      }
      this.focus('danger', 0.18);
    }
  }

  route(node, pan = 0, group = 'combat') {
    const out = this.busFor(group);
    if (this.ctx?.createStereoPanner) {
      const p = this.ctx.createStereoPanner();
      p.pan.value = Math.max(-1, Math.min(1, pan));
      node.connect(p);
      p.connect(out);
      return p;
    }
    node.connect(out);
    return out;
  }

  noiseBuffer() {
    if (this._noise) return this._noise;
    const ctx = this.ctx;
    const n = Math.floor(ctx.sampleRate * 2);
    const b = ctx.createBuffer(1, n, ctx.sampleRate);
    const d = b.getChannelData(0);
    let last = 0;
    for (let i = 0; i < n; i++) {
      const white = Math.random() * 2 - 1;
      last = last * 0.24 + white * 0.76;
      d[i] = last;
    }
    this._noise = b;
    return b;
  }

  allowSfx(id, gap) {
    const t = this.ctx?.currentTime ?? 0;
    if (t - (this._sfxLast[id] ?? -9) < gap) return false;
    this._sfxLast[id] = t;
    return true;
  }

  allowBehaviorSfx(id, stage, familyGap) {
    const t = this.ctx?.currentTime ?? 0;
    const familyKey = `behavior-${id}`;
    const laneKey = `behavior-lane-${stage}`;
    const laneGap = BEHAVIOR_AUDIO_LANE_GAP[stage] ?? familyGap;
    if (t - (this._sfxLast[familyKey] ?? -9) < familyGap
        || t - (this._sfxLast[laneKey] ?? -9) < laneGap) return false;
    // Commit both timestamps atomically: a family rejection never consumes the
    // shared lane, and a lane rejection never delays that family's next chance.
    this._sfxLast[familyKey] = t;
    this._sfxLast[laneKey] = t;
    return true;
  }

  noiseShot({ dur, gain, freq, type = 'bandpass', q = 0.8, pan = 0, attack = 0.002,
              group = 'combat' }) {
    const ctx = this.ctx, t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer();
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.setValueAtTime(freq, t);
    filter.Q.value = q;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t + attack);
    amp.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter); filter.connect(amp); this.route(amp, pan, group);
    const offset = Math.random() * Math.max(0.01, src.buffer.duration - dur - 0.02);
    src.start(t, offset, dur + 0.01);
    src.stop(t + dur + 0.02);
  }

  toneShot({ f0, f1, dur, gain, type = 'sine', pan = 0, attack = 0.003,
             group = 'combat' }) {
    const ctx = this.ctx, t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(Math.max(20, f0), t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t + attack);
    amp.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(amp); this.route(amp, pan, group);
    osc.start(t); osc.stop(t + dur + 0.02);
  }

  // ── Combat sound design ────────────────────────────────────────
  // These are deliberately separate from the numbered narrative cues. They are
  // moment-to-moment material sounds, aggregated and rate-limited so hundreds of
  // enemies produce weight rather than hundreds of oscillators.
  weaponShot(tier = 1, pan = 0, diamondSet = false) {
    const ctx = this.ensure(); if (!ctx || !this.allowSfx('weapon', 0.065)) return;
    const k = Math.max(1, Math.min(6, tier));
    const group = k >= 6 ? 'reward' : 'combat';
    // 빠른 ◆ 연사가 매 발 reward 덕킹을 연장하면 일반 명중·처치음이 전투 내내
    // 눌린다. 단일 ◆는 소리의 재질만 reward로 보내고, 포커스는 4◆ 결속 박자만 가진다.
    if (k < 6) this.focus('combat', 0.08);
    this.noiseShot({ dur: 0.075, gain: 0.055 + k * 0.008, freq: 1150 + k * 170,
                     type: 'bandpass', q: 0.65, pan, group });
    this.toneShot({ f0: 380 + k * 55, f1: 190 + k * 22, dur: 0.065,
                    gain: 0.022 + k * 0.004, type: 'triangle', pan, group });
    if (k >= 6) {
      // ◆는 기존 물성음 위에 결정 두 조각이 0.13초 간격으로 답한다. 전체 음량을
      // 키우는 대신 청백색 표식과 같은 두 박자를 귀로 센다.
      this.toneShot({ f0: 1760, f1: 940, dur: 0.11, gain: 0.026,
                      type: 'sine', pan, group: 'reward' });
      const at = ctx.currentTime + 0.13;
      const echo = ctx.createOscillator();
      const amp = ctx.createGain();
      echo.type = 'triangle';
      echo.frequency.setValueAtTime(diamondSet ? 2280 : 2080, at);
      echo.frequency.exponentialRampToValueAtTime(diamondSet ? 1180 : 1320, at + 0.095);
      amp.gain.setValueAtTime(0.0001, at);
      amp.gain.exponentialRampToValueAtTime(diamondSet ? 0.032 : 0.019, at + 0.004);
      amp.gain.exponentialRampToValueAtTime(0.0001, at + 0.12);
      echo.connect(amp); this.route(amp, pan, 'reward');
      echo.start(at); echo.stop(at + 0.13);
    }
    if (diamondSet && this.allowSfx('diamond-set-bind', 0.14)) {
      // ◆◆◆◆의 낮은 결속음. 위험 버스보다 아래인 reward에만 놓아 경보를 가리지 않는다.
      this.focus('reward', 0.18);
      this.toneShot({ f0: 132, f1: 68, dur: 0.18, gain: 0.052,
                      type: 'triangle', pan, group: 'reward' });
      this.noiseShot({ dur: 0.095, gain: 0.038, freq: 3100,
                       type: 'highpass', q: 0.55, pan, group: 'reward' });
    }
  }

  combatHit(weight = 1, pan = 0) {
    const ctx = this.ensure(); if (!ctx || !this.allowSfx('hit', 0.032)) return;
    this.focus('combat', 0.075);
    const k = Math.min(2.4, 0.82 + Math.log2(1 + Math.max(1, weight)) * 0.36);
    this.noiseShot({ dur: 0.065, gain: 0.095 * k, freq: 1850,
                     type: 'bandpass', q: 0.8, pan });
    this.toneShot({ f0: 155, f1: 58, dur: 0.085, gain: 0.085 * k,
                    type: 'triangle', pan });
    this.toneShot({ f0: 1850, f1: 720, dur: 0.027, gain: 0.020 * k,
                    type: 'square', pan, attack: 0.001 });
  }

  diamondImpact(diamondSet = false, pan = 0) {
    const ctx = this.ensure();
    const gap = diamondSet ? 0.085 : 0.12;
    if (!ctx || !this.allowSfx('diamond-impact', gap)) return;
    // 개별 ◆ 명중은 결정 잔향만 더한다. 4◆만 짧게 포커스를 가져가되 최대 연사
    // 간격보다 짧게 끝내 일반 전투 버스를 상시 압축하지 않는다.
    if (diamondSet) this.focus('reward', 0.07);
    // 본 접촉음은 combatHit가 맡고 이 층은 결정이 갈라지는 짧은 답만 낸다.
    this.toneShot({ f0: diamondSet ? 2640 : 2260, f1: diamondSet ? 1240 : 1480,
                    dur: diamondSet ? 0.13 : 0.095, gain: diamondSet ? 0.030 : 0.019,
                    type: 'triangle', pan, group: 'reward' });
    if (diamondSet) {
      this.noiseShot({ dur: 0.075, gain: 0.026, freq: 3600,
                       type: 'highpass', q: 0.7, pan, group: 'reward' });
    }
  }

  combatKill(count = 1, big = false, pan = 0) {
    const ctx = this.ensure();
    if (!ctx || (!big && !this.allowSfx('kill', 0.055))) return;
    if (big) this._sfxLast.kill = ctx.currentTime;
    const group = big ? 'reward' : 'combat';
    this.focus(group, big ? 0.25 : 0.10);
    const k = Math.min(2.8, 0.9 + Math.log2(1 + Math.max(1, count)) * 0.42 + (big ? 0.8 : 0));
    this.noiseShot({ dur: big ? 0.24 : 0.13, gain: 0.10 * k,
                     freq: big ? 540 : 860, type: 'lowpass', q: 0.55, pan, group });
    this.toneShot({ f0: big ? 105 : 132, f1: big ? 34 : 48,
                    dur: big ? 0.28 : 0.15, gain: 0.105 * k,
                    type: 'sine', pan, group });
    this.toneShot({ f0: big ? 520 : 680, f1: big ? 210 : 330,
                    dur: big ? 0.19 : 0.09, gain: 0.034 * k,
                    type: 'triangle', pan, group });
  }

  pickup(count = 1, pan = 0) {
    const ctx = this.ensure(); if (!ctx || !this.allowSfx('pickup', 0.045)) return;
    this.focus('utility', 0.08);
    const k = Math.min(2, 0.75 + Math.log2(1 + Math.max(1, count)) * 0.28);
    this.toneShot({ f0: 620, f1: 1260, dur: 0.075, gain: 0.038 * k,
                    type: 'sine', pan, attack: 0.002, group: 'utility' });
    this.toneShot({ f0: 1240, f1: 1780, dur: 0.055, gain: 0.018 * k,
                    type: 'triangle', pan, attack: 0.001, group: 'utility' });
  }

  // 장비 작업대의 짧은 재질음. 번호가 붙은 서사 큐와는 별개의 순간 UI 피드백이며,
  // 전투 SFX와 같은 버스·리미터·사용자 음소거 설정을 그대로 따른다.
  shop(action = 'open') {
    const ctx = this.ensure();
    if (!ctx || !this.allowSfx(`shop-${action}`, 0.045)) return;
    this.focus('utility', 0.12);
    if (action === 'buy') {
      this.noiseShot({ dur: 0.08, gain: 0.045, freq: 980, type: 'bandpass', q: 0.7, group: 'utility' });
      this.toneShot({ f0: 330, f1: 690, dur: 0.11, gain: 0.052, type: 'triangle', group: 'utility' });
      this.toneShot({ f0: 660, f1: 1080, dur: 0.14, gain: 0.025, type: 'sine', group: 'utility' });
      return;
    }
    if (action === 'lock') {
      this.noiseShot({ dur: 0.055, gain: 0.038, freq: 1450, type: 'bandpass', q: 1.1, group: 'utility' });
      this.toneShot({ f0: 510, f1: 360, dur: 0.07, gain: 0.038, type: 'square', group: 'utility' });
      return;
    }
    if (action === 'reroll') {
      this.noiseShot({ dur: 0.16, gain: 0.052, freq: 720, type: 'bandpass', q: 0.45, group: 'utility' });
      this.toneShot({ f0: 240, f1: 760, dur: 0.16, gain: 0.043, type: 'triangle', group: 'utility' });
      return;
    }
    const closing = action === 'close';
    this.noiseShot({ dur: 0.09, gain: 0.035, freq: closing ? 520 : 760,
                     type: 'lowpass', q: 0.5, group: 'utility' });
    this.toneShot({ f0: closing ? 430 : 190, f1: closing ? 170 : 310,
                    dur: 0.12, gain: 0.036, type: 'triangle', group: 'utility' });
  }

  playerHit(pan = 0, weight = 1) {
    const ctx = this.ensure(); if (!ctx || !this.allowSfx('hurt', 0.12)) return;
    const k = Math.max(0.75, Math.min(1.35, weight));
    this.focus('danger', 0.30);
    this.noiseShot({ dur: 0.18, gain: 0.18 * k, freq: 680, type: 'lowpass', q: 0.7,
                     pan, group: 'danger' });
    this.toneShot({ f0: 96, f1: 38, dur: 0.23, gain: 0.19 * k, type: 'sine',
                    pan, group: 'danger' });
  }

  comboRise(count = 1, pan = 0) {
    const ctx = this.ensure(); if (!ctx || count < 4 || !this.allowSfx('combo', 0.11)) return;
    this.focus('reward', 0.15);
    const k = Math.min(2.2, 1 + Math.log2(Math.max(4, count) / 4) * 0.42);
    const root = 440 + Math.min(7, count) * 34;
    this.toneShot({ f0: root, f1: root * 1.52, dur: 0.10, gain: 0.030 * k,
                    type: 'triangle', pan, attack: 0.001, group: 'reward' });
    this.toneShot({ f0: root * 1.5, f1: root * 2.18, dur: 0.12, gain: 0.018 * k,
                    type: 'sine', pan, attack: 0.002, group: 'reward' });
  }

  dangerPulse(risk = 1) {
    const ctx = this.ensure(); if (!ctx || !this.allowSfx('danger', 0.30)) return;
    this.focus('danger', 0.24);
    const k = Math.max(0, Math.min(1, risk));
    this.toneShot({ f0: 82, f1: 48, dur: 0.18, gain: 0.065 + 0.075 * k,
                    type: 'sine', attack: 0.006, group: 'danger' });
    this.noiseShot({ dur: 0.10, gain: 0.025 + 0.035 * k, freq: 190,
                     type: 'lowpass', q: 0.45, attack: 0.004, group: 'danger' });
  }

  thunder(weight = 1) {
    const ctx = this.ensure(); if (!ctx || !this.allowSfx('thunder', 2.0)) return;
    const k = Math.max(0.4, Math.min(1.4, weight));
    this.focus('boss', 1.15);
    if (this._ambience) {
      this._ambience.gain.gain.setTargetAtTime(0.018, ctx.currentTime, 0.025);
      this._ambience.target = -1; // force the bed to recover on the next update
    }
    this.noiseShot({ dur: 0.055, gain: 0.24 * k, freq: 2100,
                     type: 'bandpass', q: 0.55, attack: 0.001, group: 'boss' });
    this.noiseShot({ dur: 1.20, gain: 0.31 * k, freq: 165,
                     type: 'lowpass', q: 0.42, attack: 0.006, group: 'boss' });
    this.toneShot({ f0: 78, f1: 27, dur: 1.05, gain: 0.25 * k,
                    type: 'sine', attack: 0.004, group: 'boss' });
  }

  // 번호가 없는 보스 상태음. bearDig(14)의 물리음은 그대로 두고, 좌표 잠금·페이즈
  // 변화가 전장 소음에 묻히지 않도록 별도 보스 버스에서 짧은 구조음만 보탠다.
  bossSignal(kind = 'lock', weight = 1) {
    const ctx = this.ensure();
    if (!ctx || !this.allowSfx(`boss-${kind}`, kind === 'tick' ? 0.35 : 0.16)) return;
    const k = Math.max(0.7, Math.min(1.4, Number(weight) || 1));
    const danger = kind === 'lock';
    const group = danger ? 'danger' : 'boss';
    this.focus(group, danger ? 0.30 : 0.34);
    this.noiseShot({ dur: danger ? 0.13 : 0.24, gain: (danger ? 0.105 : 0.13) * k,
      freq: danger ? 520 : 240, type: 'lowpass', q: 0.52, attack: 0.003, group });
    this.toneShot({ f0: danger ? 150 : 118, f1: danger ? 62 : 42,
      dur: danger ? 0.20 : 0.31, gain: (danger ? 0.11 : 0.14) * k,
      type: 'triangle', attack: 0.003, group });
  }

  // 행동군의 짧은 합성음. scene이 frame bucket에서 최대 세 사건만 넘기고, 여기서는
  // 행동군/상태별 gap을 다시 적용한다. 그래서 화면에 400마리가 있어도 소리 수는
  // 개체 수가 아니라 읽을 수 있는 상태 변화 수를 따른다.
  behaviorSignal(family, stage, pan = 0, weight = 1) {
    const profile = behaviorAudioProfile(family, stage);
    if (!profile) return false;
    const ctx = this.ensure();
    const id = `${family}:${stage}`;
    if (!ctx || !this.allowBehaviorSfx(id, stage, profile.gap)) {
      this.m.behaviorDropped ??= {};
      this.m.behaviorDropped[id] = (this.m.behaviorDropped[id] ?? 0) + 1;
      return false;
    }
    const k = Math.max(0.68, Math.min(1.30, Number(weight) || 1));
    const p = Math.max(-1, Math.min(1, Number(pan) || 0));
    this.focus(profile.group, profile.focus);

    if (family === 'charge') {
      if (stage === 'tell') {
        this.noiseShot({ dur: 0.18, gain: 0.070 * k, freq: 260,
          type: 'lowpass', q: 0.48, pan: p, group: profile.group });
        this.toneShot({ f0: 72, f1: 138, dur: 0.22, gain: 0.052 * k,
          type: 'triangle', pan: p, group: profile.group });
      } else if (stage === 'commit') {
        this.noiseShot({ dur: 0.15, gain: 0.078 * k, freq: 620,
          type: 'bandpass', q: 0.45, pan: p, group: profile.group });
        this.toneShot({ f0: 128, f1: 48, dur: 0.16, gain: 0.060 * k,
          type: 'triangle', pan: p, group: profile.group });
      } else {
        this.noiseShot({ dur: 0.12, gain: 0.052 * k, freq: 410,
          type: 'lowpass', q: 0.42, pan: p, group: profile.group });
      }
    } else if (family === 'intercept') {
      if (stage === 'commit') {
        this.noiseShot({ dur: 0.095, gain: 0.072 * k, freq: 1760,
          type: 'bandpass', q: 0.88, pan: p, group: profile.group });
        this.toneShot({ f0: 310, f1: 96, dur: 0.105, gain: 0.048 * k,
          type: 'triangle', pan: p, group: profile.group });
      } else {
        this.noiseShot({ dur: 0.10, gain: 0.042 * k, freq: 520,
          type: 'lowpass', q: 0.46, pan: p, group: profile.group });
      }
    } else if (family === 'pack') {
      if (stage === 'tell') {
        this.toneShot({ f0: 330, f1: 174, dur: 0.20, gain: 0.060 * k,
          type: 'square', pan: p, group: profile.group });
        this.noiseShot({ dur: 0.16, gain: 0.050 * k, freq: 740,
          type: 'bandpass', q: 0.62, pan: p, group: profile.group });
      } else if (stage === 'commit') {
        this.noiseShot({ dur: 0.14, gain: 0.060 * k, freq: 470,
          type: 'lowpass', q: 0.40, pan: p, group: profile.group });
        this.toneShot({ f0: 118, f1: 54, dur: 0.12, gain: 0.041 * k,
          type: 'triangle', pan: p, group: profile.group });
      } else {
        this.noiseShot({ dur: 0.11, gain: 0.038 * k, freq: 360,
          type: 'lowpass', q: 0.38, pan: p, group: profile.group });
      }
    } else if (family === 'wing') {
      if (stage === 'tell') {
        this.noiseShot({ dur: 0.19, gain: 0.058 * k, freq: 1420,
          type: 'bandpass', q: 0.52, pan: p, group: profile.group });
        this.toneShot({ f0: 240, f1: 132, dur: 0.17, gain: 0.030 * k,
          type: 'triangle', pan: p, group: profile.group });
      } else if (stage === 'commit') {
        this.noiseShot({ dur: 0.13, gain: 0.062 * k, freq: 1180,
          type: 'bandpass', q: 0.40, pan: p, group: profile.group });
      } else {
        this.noiseShot({ dur: 0.15, gain: 0.040 * k, freq: 1840,
          type: 'highpass', q: 0.42, pan: p, group: profile.group });
      }
    } else if (family === 'bite') {
      if (stage === 'commit') {
        this.noiseShot({ dur: 0.075, gain: 0.052 * k, freq: 760,
          type: 'bandpass', q: 0.58, pan: p, group: profile.group });
        this.toneShot({ f0: 126, f1: 68, dur: 0.085, gain: 0.036 * k,
          type: 'triangle', pan: p, group: profile.group });
      } else {
        this.noiseShot({ dur: 0.085, gain: 0.026 * k, freq: 340,
          type: 'lowpass', q: 0.38, pan: p, group: profile.group });
      }
    } else if (family === 'kick') {
      if (stage === 'commit') {
        this.noiseShot({ dur: 0.10, gain: 0.061 * k, freq: 290,
          type: 'lowpass', q: 0.36, pan: p, group: profile.group });
        this.toneShot({ f0: 104, f1: 43, dur: 0.11, gain: 0.043 * k,
          type: 'triangle', pan: p, group: profile.group });
      } else {
        this.noiseShot({ dur: 0.08, gain: 0.025 * k, freq: 460,
          type: 'lowpass', q: 0.34, pan: p, group: profile.group });
      }
    } else if (family === 'talon') {
      if (stage === 'tell') {
        // Folded wings loading into the already visible low dive.
        this.noiseShot({ dur: 0.18, gain: 0.055 * k, freq: 1360,
          type: 'bandpass', q: 0.50, pan: p, group: profile.group });
        this.toneShot({ f0: 218, f1: 126, dur: 0.16, gain: 0.027 * k,
          type: 'triangle', pan: p, group: profile.group });
      } else if (stage === 'commit') {
        // Two dry components: ground contact below, talons scraping above.
        this.noiseShot({ dur: 0.095, gain: 0.060 * k, freq: 285,
          type: 'lowpass', q: 0.36, pan: p, group: profile.group });
        this.noiseShot({ dur: 0.12, gain: 0.046 * k, freq: 2140,
          type: 'highpass', q: 0.58, pan: p, group: profile.group });
      } else {
        this.noiseShot({ dur: 0.15, gain: 0.036 * k, freq: 1760,
          type: 'highpass', q: 0.40, pan: p, group: profile.group });
      }
    }
    this.m.behaviorPlayed ??= {};
    this.m.behaviorPlayed[id] = (this.m.behaviorPlayed[id] ?? 0) + 1;
    return true;
  }

  // signal은 기존 boss-lock/bearDig가 맡는다. 실제 attackGeometry가 commit/recovery로
  // 넘어간 한 번만 이 메서드를 불러, 잠긴 모양의 종류에 맞는 접촉/회수 재질을 낸다.
  bossAction(stage, shape = 'point', pan = 0, weight = 1) {
    if (stage !== 'commit' && stage !== 'recovery') return false;
    const ctx = this.ensure();
    const id = `${stage}:${shape}`;
    const gap = stage === 'commit' ? 0.14 : 0.36;
    if (!ctx || !this.allowSfx(`boss-action-${id}`, gap)) return false;
    const k = Math.max(0.72, Math.min(1.45, Number(weight) || 1));
    const p = Math.max(-1, Math.min(1, Number(pan) || 0));
    const group = stage === 'commit' ? 'boss' : 'combat';
    this.focus(group, stage === 'commit' ? 0.28 : 0.12);
    if (stage === 'commit') {
      const paws = shape === 'paw-pair' || shape === 'rush-chain';
      const claws = shape === 'pounce-rake' || shape === 'claw-pair'
        || shape === 'bound-chain';
      this.noiseShot({ dur: paws ? 0.26 : 0.18, gain: (paws ? 0.14 : 0.11) * k,
        freq: paws ? 210 : claws ? 940 : 610,
        type: paws ? 'lowpass' : 'bandpass',
        q: paws ? 0.38 : 0.54, pan: p, group });
      this.toneShot({ f0: paws ? 94 : claws ? 184 : 138, f1: paws ? 32 : 48,
        dur: paws ? 0.30 : 0.22, gain: 0.105 * k,
        type: 'triangle', pan: p, group });
    } else {
      this.noiseShot({ dur: 0.16, gain: 0.054 * k, freq: 360,
        type: 'lowpass', q: 0.42, pan: p, group });
      this.toneShot({ f0: 96, f1: 55, dur: 0.14, gain: 0.040 * k,
        type: 'sine', pan: p, group });
    }
    this.m.bossActions ??= {};
    this.m.bossActions[id] = (this.m.bossActions[id] ?? 0) + 1;
    return true;
  }

  // ⚠⚠ **비 오는 밤을 날짜로 판별하지 않는다** 【40일 개편 2026-08-03】.
  //   구 판은 `nightKey === 'N15'` 였다. 40일에서 N15 는 **협곡의 밤**이 됐고
  //   비의 밤은 22일로 갔다 — 그대로 뒀으면 **엉뚱한 밤에 비가 오고 비 오는 밤은
  //   조용했을 것이다.** 정본은 카드의 날씨이고, `config.isRainNight` 이 그걸 읽는다.
  ambience(nightKey, want = 1) {
    if (!this.on || want <= 0.001) {
      if (this._ambience && this.ctx) {
        this._ambience.gain.gain.setTargetAtTime(0.0001, this.ctx.currentTime, 0.18);
      }
      return;
    }
    const ctx = this.ensure(); if (!ctx) return;
    const profile = AMBIENCE[cardIdOf(nightKey)] ?? AMBIENCE[nightKey] ?? AMBIENCE.N1;
    if (this._ambience?.key !== nightKey) {
      if (this._ambience) {
        for (const v of this._ambience.voices) { try { v.stop(); } catch { /* old bed */ } }
        this._ambience.gain.disconnect();
      }
      const output = ctx.createGain();
      output.gain.value = 0.0001;
      output.connect(this.busFor('ambient'));

      const air = ctx.createBufferSource();
      air.buffer = this.noiseBuffer(); air.loop = true;
      air.playbackRate.value = profile.rate;
      const airFilter = ctx.createBiquadFilter();
      airFilter.type = profile.airType;
      airFilter.frequency.value = profile.airFreq;
      airFilter.Q.value = profile.airType === 'bandpass' ? 0.34 : 0.22;
      const airGain = ctx.createGain(); airGain.gain.value = isRainNight(nightKey) ? 0.92 : 0.46;
      air.connect(airFilter); airFilter.connect(airGain); airGain.connect(output);

      const body = ctx.createBufferSource();
      body.buffer = this.noiseBuffer(); body.loop = true;
      body.playbackRate.value = profile.rate * 0.71;
      const bodyFilter = ctx.createBiquadFilter();
      bodyFilter.type = 'bandpass'; bodyFilter.frequency.value = profile.bodyFreq; bodyFilter.Q.value = 0.28;
      const bodyGain = ctx.createGain(); bodyGain.gain.value = isRainNight(nightKey) ? 0.68 : 0.52;
      body.connect(bodyFilter); bodyFilter.connect(bodyGain); bodyGain.connect(output);

      const voices = [air, body];
      air.start(); body.start();
      if (profile.drone) {
        const drone = ctx.createOscillator();
        drone.type = 'sine'; drone.frequency.value = profile.drone;
        const droneGain = ctx.createGain(); droneGain.gain.value = 0.12;
        drone.connect(droneGain); droneGain.connect(output); drone.start();
        voices.push(drone);
      }
      this._ambience = { key: nightKey, gain: output, voices, profile, target: -1, lastSet: -9 };
    }
    const target = profile.master * want;
    if (Math.abs(target - this._ambience.target) > 0.0005
        || ctx.currentTime - this._ambience.lastSet > 0.50) {
      this._ambience.gain.gain.setTargetAtTime(target, ctx.currentTime, 0.28);
      this._ambience.target = target;
      this._ambience.lastSet = ctx.currentTime;
    }
  }

  // ⚠ 「옛 호출부를 위해」 남긴 `rain()` 래퍼가 여기 있었다 — 부르는 곳 0(폐기 2026-08-07).

  // 덕킹 — 나보다 상위(pri 가 작은) 큐가 울리는 중이면 −50%.
  duck(pri) {
    const t = this.ctx ? this.ctx.currentTime : 0;
    for (const p in this.until) if (+p < pri && this.until[p] > t) return 0.5;
    return 1;
  }
  muted(id) {
    if (!this.on || this.prefs.muted) return true;
    if (this.silence && HUSHED_BY_SILENCE.includes(id)) return true;
    return false;
  }

  // ── 단발 ────────────────────────────────────────────────────────
  // **최소 간격(minGap)** — N30은 하이에나가 110마리다. 예고가 겹치면 오실레이터가
  // 수십 개씩 쌓여 소리도 프레임도 무너진다. 겹침을 **버리는** 것이 맞다:
  // 같은 신호가 동시에 둘 이상 들려도 플레이어가 읽는 정보는 하나다.
  play(id, gain = 1) {
    const cue = CUES[id];
    if (!cue || !cue.voice || this.muted(id)) return false;
    const ctx = this.ensure(); if (!ctx) return false;
    const v = cue.voice, t = ctx.currentTime;
    const gap = cue.minGap ?? 0;
    this._last ??= {};
    if (gap && t - (this._last[id] ?? -9) < gap) { this.m.dropped = (this.m.dropped ?? 0) + 1; return false; }
    this._last[id] = t;
    const group = cueAudioLayer(id);
    if (group !== 'ambient') this.focus(group, Math.max(0.08, v.dur));
    const g = ctx.createGain();
    const amp = v.gain * gain * this.duck(cue.pri);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, amp), t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + v.dur);
    g.connect(this.busFor(group));
    const voice = this.makeVoice(v, t);
    voice.node.connect(g);
    voice.start(t); voice.stop(t + v.dur + 0.02);
    this.until[cue.pri] = Math.max(this.until[cue.pri] ?? 0, t + v.dur);
    this.m.played[id] = (this.m.played[id] ?? 0) + 1;
    return true;
  }

  // { node, start, stop } — 오실레이터든 노이즈든 호출부가 같은 모양을 본다.
  makeVoice(v, t) {
    const ctx = this.ctx;
    if (v.type === 'noise') {
      // dur 0(= 지속 큐)이면 1초 버퍼를 루프한다. 버퍼 안의 감쇠 램프가 루프 주기마다
      // 되풀이돼 「숨」처럼 들이쉬고 내쉬는 꼴이 된다 — 자리표시자로는 이 편이 낫다.
      const n = Math.floor(ctx.sampleRate * Math.max(v.dur || 1, 0.3));
      const buf = ctx.createBuffer(1, n, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
      const src = ctx.createBufferSource();
      src.buffer = buf; src.loop = !v.dur;
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass'; f.frequency.value = v.f0;
      src.connect(f);
      return { node: f, start: x => src.start(x), stop: x => { try { src.stop(x); } catch { /* 이미 멈춤 */ } } };
    }
    const o = ctx.createOscillator();
    o.type = v.type; o.frequency.setValueAtTime(v.f0, t);
    if (v.f1) o.frequency.exponentialRampToValueAtTime(v.f1, t + (v.dur || 0.4));
    let lfo = null;
    if (v.vib) {                     // 떨림 — 「울음」이 삑 소리와 구별되게만
      const la = ctx.createGain();
      lfo = ctx.createOscillator();
      lfo.frequency.value = v.vib; la.gain.value = v.f0 * 0.04;
      lfo.connect(la); la.connect(o.frequency); lfo.start(t);
    }
    // **떨림 오실레이터도 같이 멈춘다.** 반송파만 멈추면 LFO 가 o.frequency 에 물린 채
    // 영원히 돌고 GC 도 안 된다 — 단발 하나당 노드 둘이 새고, 밤마다 늘어난다.
    const stop = x => {
      try { o.stop(x); } catch { /* 이미 멈춤 */ }
      if (lfo) { try { lfo.stop(x); } catch { /* 이미 멈춤 */ } }
    };
    return { node: o, start: x => o.start(x), stop };
  }

  // ── 지속 ────────────────────────────────────────────────────────
  // want = 0 이면 소리가 멈춘다(끄는 게 아니라 **음량 0**이다 — 큐 9의 "2초 그침"이
  // 껐다 켜는 것이 아니라 잦아드는 것이어야 하므로).
  loop(id, want) {
    const cue = CUES[id];
    if (!cue || !cue.voice) return;
    if (this.muted(id) || want <= 0.0005) {
      const L = this.loops[id];
      if (L) L.gain.gain.setTargetAtTime(0.0001, this.ctx.currentTime, 0.08);
      return;
    }
    const ctx = this.ensure(); if (!ctx) return;
    let L = this.loops[id];
    if (!L) {
      const v = cue.voice, g = ctx.createGain();
      g.gain.value = 0.0001; g.connect(this.busFor(cueAudioLayer(id)));
      const voice = this.makeVoice(v, ctx.currentTime);
      voice.node.connect(g); voice.start(ctx.currentTime);
      L = this.loops[id] = { voice, gain: g, cue };
    }
    const amp = Math.max(0.0002, cue.voice.gain * want * this.duck(cue.pri));
    L.gain.gain.setTargetAtTime(amp, ctx.currentTime, 0.12);
  }

  stopAll() {
    for (const id in this.loops) {
      this.loops[id].voice.stop(this.ctx ? this.ctx.currentTime : 0);
      delete this.loops[id];
    }
    if (this._ambience) {
      for (const v of this._ambience.voices) { try { v.stop(); } catch { /* already stopped */ } }
      this._ambience.gain.disconnect();
      this._ambience = null;
    }
  }

  dispose() {
    this.stopAll();
    if (this.mix) {
      for (const id of AUDIO_LAYERS) {
        try { this.mix[id]?.disconnect(); } catch { /* already disconnected */ }
        try { this.trim[id]?.disconnect(); } catch { /* already disconnected */ }
        try { this.pauseGate[id]?.disconnect(); } catch { /* already disconnected */ }
      }
      this.mix = null;
      this.trim = null;
      this.pauseGate = null;
    }
  }

  // 시각 이중화를 그려야 하는가. dualBase 는 접근성 모드와 **무관하게** 항상 참이다.
  showDual(id) {
    const c = CUES[id];
    if (!c || !c.dual) return false;
    return c.dualBase === true || this.a11y;
  }
}
